import { useEffect, useState, useRef } from "react";
import { useOutletContext } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { QRCodeCanvas } from "qrcode.react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import RichTextEditor from "@/components/RichTextEditor";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Upload, Download, Link2, MessageCircle, Mail, QrCode, BellRing, Pencil, Trash2, Loader2, Copy, FileSpreadsheet, BedDouble, CheckCircle2, Circle, Ban, Gift, CalendarPlus } from "lucide-react";
import { toast } from "sonner";

/** Guests aged this many years or younger are counted as children on RSVP. */
const CHILD_MAX_AGE_YEARS = 6;

const empty = { family_name: "", primary_guest_name: "", mobile: "", email: "", adults_invited: 1, children_invited: 0, notes: "", rsvp_deadline: "" };

const statusBadge = (status) => {
  const map = {
    attending: "bg-[#1A362D] text-white",
    declined: "bg-red-100 text-red-700",
    pending: "bg-[#F4EFE6] text-[#57534E]",
  };
  return map[status] || map.pending;
};

export default function Guests() {
  const { eventId, currentEvent } = useOutletContext();
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [qrGuest, setQrGuest] = useState(null);
  const [emailingAll, setEmailingAll] = useState(false);
  const [reminding, setReminding] = useState(false);
  const [assignGuest, setAssignGuest] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [assignRoomId, setAssignRoomId] = useState("");
  const [sentFilter, setSentFilter] = useState("all"); // all | sent | unsent
  const [togglingSent, setTogglingSent] = useState({});
  const [togglingBlocked, setTogglingBlocked] = useState({});
  const [assignSlots, setAssignSlots] = useState("2");
  const [assignExtraBeds, setAssignExtraBeds] = useState("0");
  const [selected, setSelected] = useState({});
  const [extendOpen, setExtendOpen] = useState(false);
  const [extendDate, setExtendDate] = useState("");
  const [giftGuest, setGiftGuest] = useState(null);
  const [giftForm, setGiftForm] = useState({ description: "", notes: "" });
  const fileRef = useRef();

  const load = async () => {
    setLoading(true);
    const [g, r, a] = await Promise.all([
      api.get(`/guests/${eventId}`),
      api.get(`/rooms/${eventId}`).catch(() => ({ data: [] })),
      api.get(`/room-assignments/${eventId}`).catch(() => ({ data: [] })),
    ]);
    setGuests(g.data);
    setRooms(r.data || []);
    setAssignments(a.data || []);
    setLoading(false);
  };

  useEffect(() => {
    if (eventId) load();
  }, [eventId]);

  const openNew = () => {
    setForm(empty);
    setEditId(null);
    setOpen(true);
  };

  const openEdit = (g) => {
    setForm({ ...g });
    setEditId(g.id);
    setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        event_id: eventId,
        family_name: form.family_name,
        primary_guest_name: form.primary_guest_name,
        mobile: form.mobile,
        email: form.email,
        adults_invited: Number(form.adults_invited) || 0,
        children_invited: Number(form.children_invited) || 0,
        notes: form.notes,
        rsvp_deadline: form.rsvp_deadline || "",
      };
      if (editId) await api.put(`/guests/${editId}`, payload);
      else await api.post("/guests", payload);
      toast.success(editId ? "Guest updated" : "Guest added");
      setOpen(false);
      load();
    } catch (e) {
      toast.error("Could not save guest");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    await api.delete(`/guests/${id}`);
    toast.success("Guest removed");
    load();
  };

  const inviteUrl = (token) => `${window.location.origin}/invite/${token}`;

  const parseEventDate = (d) => new Date(`${d}T12:00:00`);

  const formatEventDates = (start, end) => {
    if (!start) return "";
    const startDate = parseEventDate(start);
    if (!end || end === start) {
      return startDate.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
    }
    const endDate = parseEventDate(end);
    const sameYear = startDate.getFullYear() === endDate.getFullYear();
    const sameMonth = sameYear && startDate.getMonth() === endDate.getMonth();
    if (sameMonth) {
      return `${startDate.toLocaleDateString("en-US", { month: "long", day: "numeric" })}–${endDate.getDate()}, ${endDate.getFullYear()}`;
    }
    if (sameYear) {
      const left = startDate.toLocaleDateString("en-US", { month: "long", day: "numeric" });
      const right = endDate.toLocaleDateString("en-US", { month: "long", day: "numeric" });
      return `${left}–${right}, ${endDate.getFullYear()}`;
    }
    const left = startDate.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
    const right = endDate.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
    return `${left}–${right}`;
  };

  /** End date is checkout; celebration duration is nights stayed (end − start). */
  const celebrationDuration = (start, end) => {
    const words = ["one", "two", "three", "four", "five", "six", "seven", "eight", "nine", "ten"];
    if (!start || !end || end === start) return "one day";
    const days = Math.round((parseEventDate(end) - parseEventDate(start)) / 86400000);
    const n = Math.max(1, days);
    const label = n <= words.length ? words[n - 1] : String(n);
    return n === 1 ? `${label} day` : `${label} days`;
  };

  const waMessage = (g) => {
    const names = currentEvent
      ? [currentEvent.partner_one, currentEvent.partner_two].filter(Boolean).join(" & ") || "us"
      : "us";
    const dates = formatEventDates(currentEvent?.start_date, currentEvent?.end_date);
    const duration = celebrationDuration(currentEvent?.start_date, currentEvent?.end_date);
    const when = dates ? `\n\n${dates}` : "";
    return `Dear ${g.primary_guest_name},

BEFORE THE VOWS

Join us as we celebrate ${names} with ${duration} of love, laughter and togetherness.${when}

View your personal invitation & RSVP here: ${inviteUrl(g.token)}`;
  };

  const copyLink = (g) => {
    navigator.clipboard.writeText(inviteUrl(g.token));
    toast.success("Invitation link copied");
  };
  const copyMessage = (g) => {
    navigator.clipboard.writeText(waMessage(g));
    toast.success("WhatsApp message copied");
  };
  const openWhatsApp = async (g) => {
    const phone = (g.mobile || "").replace(/[^0-9]/g, "");
    const url = `https://wa.me/${phone}?text=${encodeURIComponent(waMessage(g))}`;
    window.open(url, "_blank");
    if (!g.invitation_sent) {
      try {
        await api.patch(`/guests/${g.id}/sent`, { sent: true });
        setGuests((prev) => prev.map((x) => (x.id === g.id ? { ...x, invitation_sent: true } : x)));
      } catch {
        /* manual Mark sent still available */
      }
    }
  };

  const setInviteSent = async (g, sent) => {
    setTogglingSent((s) => ({ ...s, [g.id]: true }));
    try {
      await api.patch(`/guests/${g.id}/sent`, { sent });
      setGuests((prev) => prev.map((x) => (x.id === g.id ? { ...x, invitation_sent: sent } : x)));
      toast.success(sent ? "Marked as invite sent" : "Marked as not sent");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not update sent status");
    } finally {
      setTogglingSent((s) => ({ ...s, [g.id]: false }));
    }
  };

  const setInviteBlocked = async (g, blocked) => {
    setTogglingBlocked((s) => ({ ...s, [g.id]: true }));
    try {
      await api.patch(`/guests/${g.id}/blocked`, { blocked });
      setGuests((prev) => prev.map((x) => (x.id === g.id ? { ...x, invite_blocked: blocked } : x)));
      toast.success(blocked ? "Invite link blocked" : "Invite link restored");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not update invite access");
    } finally {
      setTogglingBlocked((s) => ({ ...s, [g.id]: false }));
    }
  };

  const sendEmail = async (g) => {
    if (!g.email) return toast.error("Add an email address for this guest first");
    const tid = toast.loading("Sending invitation email...");
    try {
      await api.post(`/guests/${g.id}/send-email`, { base_url: window.location.origin });
      toast.success("Invitation emailed", { id: tid });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Email failed", { id: tid });
    }
  };

  const sendEmailAll = async () => {
    setEmailingAll(true);
    const tid = toast.loading("Sending invitations to all guests...");
    try {
      const { data } = await api.post(`/guests/send-email-all/${eventId}`, { base_url: window.location.origin });
      toast.success(`Sent ${data.sent} · skipped ${data.skipped} (no email) · undeliverable ${data.undeliverable || 0} · failed ${data.failed}`, { id: tid });
      load();
    } catch {
      toast.error("Bulk email failed", { id: tid });
    } finally {
      setEmailingAll(false);
    }
  };

  const sendReminders = async () => {
    setReminding(true);
    const tid = toast.loading("Sending reminders to pending guests...");
    try {
      const { data } = await api.post(`/guests/send-reminders/${eventId}`, { base_url: window.location.origin });
      toast.success(`Reminders sent ${data.sent} · skipped ${data.skipped} · undeliverable ${data.undeliverable || 0}`, { id: tid });
      load();
    } catch {
      toast.error("Could not send reminders", { id: tid });
    } finally {
      setReminding(false);
    }
  };

  const downloadQr = () => {
    const canvas = document.querySelector("#qr-canvas-wrap canvas");
    if (!canvas || !qrGuest) return;
    const a = document.createElement("a");
    a.href = canvas.toDataURL("image/png");
    a.download = `invite-${qrGuest.primary_guest_name}.png`;
    a.click();
  };

  const onImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/guests/import/${eventId}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Imported ${data.inserted} guests`);
      load();
    } catch (err) {
      toast.error("Import failed. Check file columns.");
    }
    e.target.value = "";
  };

  const exportList = async () => {
    const res = await api.get(`/guests/export/${eventId}`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = "guest_list.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadTemplate = () => {
    const a = document.createElement("a");
    a.href = `${process.env.PUBLIC_URL || ""}/templates/guest_list_template.csv`;
    a.download = "guest_list_template.csv";
    a.click();
  };

  const visibleGuests = guests.filter((g) => {
    if (sentFilter === "sent") return !!g.invitation_sent;
    if (sentFilter === "unsent") return !g.invitation_sent;
    return true;
  });

  return (
    <div data-testid="guests-page">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Guests</h1>
          <p className="text-sm text-[#525252]">
            {guests.length} invited · {guests.filter((g) => g.invitation_sent).length} invites sent ·{" "}
            {guests.filter((g) => !g.invitation_sent).length} remaining
            {guests.some((g) => g.invite_blocked) ? ` · ${guests.filter((g) => g.invite_blocked).length} blocked` : ""}
          </p>
          <p className="mt-1 text-xs text-[#737373]">
            Send via WhatsApp, then mark Invite sent. Children = guests aged {CHILD_MAX_AGE_YEARS} years and under.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" onChange={onImport} className="hidden" data-testid="import-file-input" />
          <Button variant="outline" onClick={downloadTemplate} data-testid="download-template-button">
            <FileSpreadsheet className="mr-2 h-4 w-4" /> Template
          </Button>
          <Button variant="outline" onClick={() => fileRef.current.click()} data-testid="import-guests-button">
            <Upload className="mr-2 h-4 w-4" /> Import
          </Button>
          <Button variant="outline" onClick={exportList} data-testid="export-guests-button">
            <Download className="mr-2 h-4 w-4" /> Export
          </Button>
          <Button variant="outline" onClick={sendEmailAll} disabled={emailingAll} data-testid="email-all-button">
            {emailingAll ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Mail className="mr-2 h-4 w-4" />} Email All
          </Button>
          <Button variant="outline" onClick={sendReminders} disabled={reminding} data-testid="send-reminders-button">
            {reminding ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <BellRing className="mr-2 h-4 w-4" />} Send Reminders
          </Button>
          <Button
            variant="outline"
            disabled={!Object.values(selected).some(Boolean)}
            onClick={() => {
              setExtendDate("");
              setExtendOpen(true);
            }}
            data-testid="extend-deadline-button"
          >
            <CalendarPlus className="mr-2 h-4 w-4" /> Extend RSVP date
          </Button>
          <Button onClick={openNew} data-testid="add-guest-button" className="bg-[#1A362D] hover:bg-[#12271f]">
            <Plus className="mr-2 h-4 w-4" /> Add Guest
          </Button>
        </div>
      </div>

      <div className="mb-3 flex flex-wrap gap-2">
        {[
          { id: "all", label: "All" },
          { id: "unsent", label: "Not sent" },
          { id: "sent", label: "Invite sent" },
        ].map((f) => (
          <button
            key={f.id}
            type="button"
            onClick={() => setSentFilter(f.id)}
            data-testid={`filter-sent-${f.id}`}
            className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
              sentFilter === f.id
                ? "bg-[#1A362D] text-white"
                : "border border-[#E5E5E5] bg-white text-[#525252] hover:bg-[#F4EFE6]"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="rounded-md border border-[#E5E5E5] bg-white">
        {loading ? (
          <div className="flex h-40 items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-[#1A362D]" />
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">
                  <input
                    type="checkbox"
                    aria-label="Select visible guests"
                    checked={visibleGuests.length > 0 && visibleGuests.every((g) => selected[g.id])}
                    onChange={(e) => {
                      const next = { ...selected };
                      visibleGuests.forEach((g) => { next[g.id] = e.target.checked; });
                      setSelected(next);
                    }}
                    data-testid="select-all-guests"
                  />
                </TableHead>
                <TableHead className="w-12">#</TableHead>
                <TableHead>Guest</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Invited</TableHead>
                <TableHead>Coming</TableHead>
                <TableHead>RSVP</TableHead>
                <TableHead>Room</TableHead>
                <TableHead>Invite sent</TableHead>
                <TableHead>Link</TableHead>
                <TableHead className="text-right">Share & Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visibleGuests.map((g, i) => {
                const guestRooms = assignments
                  .filter((a) => a.guest_id === g.id)
                  .map((a) => {
                    const room = rooms.find((r) => r.id === a.room_id);
                    return room?.room_number || "?";
                  });
                return (
                <TableRow key={g.id} className="hover:bg-[#FAFAFA]" data-testid={`guest-row-${g.id}`}>
                  <TableCell>
                    <input
                      type="checkbox"
                      checked={!!selected[g.id]}
                      onChange={(e) => setSelected({ ...selected, [g.id]: e.target.checked })}
                      aria-label={`Select ${g.primary_guest_name}`}
                      data-testid={`select-guest-${g.id}`}
                    />
                  </TableCell>
                  <TableCell className="text-sm text-[#737373]" data-testid={`guest-serial-${g.id}`}>{i + 1}</TableCell>
                  <TableCell>
                    <div className="font-medium">{g.primary_guest_name}</div>
                    <div className="text-xs text-[#525252]">{g.family_name}</div>
                  </TableCell>
                  <TableCell className="text-sm">
                    <div>{g.mobile}</div>
                    <div className="text-xs text-[#525252]">{g.email}</div>
                  </TableCell>
                  <TableCell className="text-sm">{g.adults_invited}A · {g.children_invited}C</TableCell>
                  <TableCell className="text-sm" data-testid={`guest-coming-${g.id}`}>
                    {g.rsvp?.status === "attending"
                      ? `${g.rsvp.adults_attending || 0}A · ${g.rsvp.children_attending || 0}C`
                      : "—"}
                  </TableCell>
                  <TableCell>
                    <Badge className={statusBadge(g.rsvp?.status)}>{g.rsvp?.status || "pending"}</Badge>
                    {g.rsvp_deadline && (
                      <div className="mt-1 text-xs text-[#525252]" data-testid={`guest-deadline-${g.id}`}>RSVP by {g.rsvp_deadline}</div>
                    )}
                    {g.rsvp?.travel?.arrival_mode && (
                      <div className="mt-1 text-xs text-[#525252] capitalize">{g.rsvp.travel.arrival_mode}{g.rsvp.travel.departure_date ? ` · drop-off ${g.rsvp.travel.departure_date}` : ""}</div>
                    )}
                    {g.rsvp?.special_request && (
                      <div className="mt-1 text-xs text-[#525252]">Request on file</div>
                    )}
                    {g.gift?.description && (
                      <div className="mt-1 text-xs text-[#525252]">Gift: {g.gift.description}</div>
                    )}
                    {g.rsvp?.status === "attending" && (g.rsvp.accessible_room_required || g.rsvp.transportation_required || g.rsvp.accommodation_required) && (
                      <div className="mt-1 text-xs text-[#525252]">
                        {[
                          g.rsvp.accessible_room_required && "Accessible",
                          g.rsvp.transportation_required && "Transport",
                          g.rsvp.accommodation_required && "Stay",
                        ].filter(Boolean).join(" · ")}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-sm" data-testid={`guest-room-${g.id}`}>
                    {guestRooms.length > 0 ? guestRooms.join(", ") : "—"}
                  </TableCell>
                  <TableCell>
                    <Button
                      type="button"
                      size="sm"
                      variant={g.invitation_sent ? "default" : "outline"}
                      disabled={!!togglingSent[g.id]}
                      onClick={() => setInviteSent(g, !g.invitation_sent)}
                      title={g.invitation_sent ? "Mark as not sent" : "Mark invite sent"}
                      data-testid={`toggle-sent-${g.id}`}
                      className={g.invitation_sent ? "bg-[#1A362D] hover:bg-[#12271f]" : ""}
                    >
                      {togglingSent[g.id] ? (
                        <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                      ) : g.invitation_sent ? (
                        <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" />
                      ) : (
                        <Circle className="mr-1.5 h-3.5 w-3.5" />
                      )}
                      {g.invitation_sent ? "Sent" : "Mark sent"}
                    </Button>
                  </TableCell>
                  <TableCell>
                    <Button
                      type="button"
                      size="sm"
                      variant={g.invite_blocked ? "default" : "outline"}
                      disabled={!!togglingBlocked[g.id]}
                      onClick={() => setInviteBlocked(g, !g.invite_blocked)}
                      title={g.invite_blocked ? "Restore invite link" : "Block invite link"}
                      data-testid={`toggle-blocked-${g.id}`}
                      className={g.invite_blocked ? "bg-red-700 hover:bg-red-800" : ""}
                    >
                      {togglingBlocked[g.id] ? (
                        <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Ban className="mr-1.5 h-3.5 w-3.5" />
                      )}
                      {g.invite_blocked ? "Blocked" : "Block"}
                    </Button>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        size="icon"
                        variant="ghost"
                        title="Assign room"
                        onClick={() => {
                          setAssignGuest(g);
                          setAssignRoomId("");
                          setAssignSlots("2");
                          setAssignExtraBeds(String(g.rsvp?.extra_beds || 0));
                        }}
                        data-testid={`assign-room-${g.id}`}
                      >
                        <BedDouble className="h-4 w-4" />
                      </Button>
                      <Button size="icon" variant="ghost" title="Copy link" onClick={() => copyLink(g)} data-testid={`copy-link-${g.id}`}>
                        <Link2 className="h-4 w-4" />
                      </Button>
                      <Button size="icon" variant="ghost" title="Copy WhatsApp message" onClick={() => copyMessage(g)} data-testid={`copy-message-${g.id}`}>
                        <Copy className="h-4 w-4" />
                      </Button>
                      <Button size="icon" variant="ghost" title="Open WhatsApp" onClick={() => openWhatsApp(g)} data-testid={`open-whatsapp-${g.id}`}>
                        <MessageCircle className="h-4 w-4 text-green-600" />
                      </Button>
                      <Button size="icon" variant="ghost" title="Email invitation" onClick={() => sendEmail(g)} data-testid={`email-guest-${g.id}`}>
                        <Mail className="h-4 w-4 text-blue-600" />
                      </Button>
                      <Button size="icon" variant="ghost" title="QR code" onClick={() => setQrGuest(g)} data-testid={`qr-guest-${g.id}`}>
                        <QrCode className="h-4 w-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        title="Record gift"
                        onClick={() => {
                          setGiftGuest(g);
                          setGiftForm({
                            description: g.gift?.description || "",
                            notes: g.gift?.notes || "",
                          });
                        }}
                        data-testid={`gift-guest-${g.id}`}
                      >
                        <Gift className="h-4 w-4" />
                      </Button>
                      <Button size="icon" variant="ghost" title="Edit" onClick={() => openEdit(g)} data-testid={`edit-guest-${g.id}`}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button size="icon" variant="ghost" title="Delete" onClick={() => remove(g.id)} data-testid={`delete-guest-${g.id}`}>
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
                );
              })}
              {guests.length === 0 && (
                <TableRow>
                  <TableCell colSpan={11} className="py-10 text-center text-[#525252]">No guests yet. Add one or import from Excel.</TableCell>
                </TableRow>
              )}
              {guests.length > 0 && visibleGuests.length === 0 && (
                <TableRow>
                  <TableCell colSpan={11} className="py-10 text-center text-[#525252]">No guests match this filter.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="guest-dialog">
          <DialogHeader>
            <DialogTitle>{editId ? "Edit Guest" : "Add Guest"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 space-y-2">
              <Label>Primary Guest Name *</Label>
              <Input value={form.primary_guest_name} onChange={(e) => setForm({ ...form, primary_guest_name: e.target.value })} data-testid="guest-name-input" />
            </div>
            <div className="col-span-2 space-y-2">
              <Label>Family Name</Label>
              <Input value={form.family_name} onChange={(e) => setForm({ ...form, family_name: e.target.value })} data-testid="guest-family-input" />
            </div>
            <div className="space-y-2">
              <Label>Mobile</Label>
              <Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} placeholder="+91..." data-testid="guest-mobile-input" />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="guest-email-input" />
            </div>
            <div className="space-y-2">
              <Label>Adults Invited</Label>
              <Input type="number" min="0" value={form.adults_invited} onChange={(e) => setForm({ ...form, adults_invited: e.target.value })} data-testid="guest-adults-input" />
            </div>
            <div className="space-y-2">
              <Label>Children Invited</Label>
              <Input type="number" min="0" value={form.children_invited} onChange={(e) => setForm({ ...form, children_invited: e.target.value })} data-testid="guest-children-input" />
              <p className="text-xs text-[#737373]">Count guests aged {CHILD_MAX_AGE_YEARS} years and under as children.</p>
            </div>
            <div className="col-span-2 space-y-2">
              <Label>Personal RSVP date</Label>
              <Input
                type="date"
                value={form.rsvp_deadline || ""}
                onChange={(e) => setForm({ ...form, rsvp_deadline: e.target.value })}
                data-testid="guest-deadline-input"
              />
              <p className="text-xs text-[#737373]">
                Leave blank to use the event deadline{currentEvent?.rsvp_deadline ? ` (${currentEvent.rsvp_deadline})` : ""}. Set a later date for a new invitee or someone who still needs to respond.
              </p>
            </div>
            <div className="col-span-2 space-y-2">
              <Label>Notes</Label>
              <RichTextEditor
                key={`guest-notes-${editId || "new"}`}
                value={form.notes || ""}
                onChange={(html) => setForm({ ...form, notes: html })}
                minHeight="90px"
                data-testid="guest-notes-editor"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving || !form.primary_guest_name} className="bg-[#1A362D] hover:bg-[#12271f]" data-testid="save-guest-button">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!qrGuest} onOpenChange={(o) => !o && setQrGuest(null)}>
        <DialogContent className="max-w-xs" data-testid="qr-dialog">
          <DialogHeader>
            <DialogTitle>QR Invitation</DialogTitle>
          </DialogHeader>
          {qrGuest && (
            <div className="flex flex-col items-center gap-4">
              <div id="qr-canvas-wrap" className="rounded-xl border border-[#E5E5E5] bg-white p-4">
                <QRCodeCanvas value={inviteUrl(qrGuest.token)} size={220} level="M" />
              </div>
              <p className="text-center text-sm text-[#525252]">Scan to open {qrGuest.primary_guest_name}'s invitation</p>
              <Button onClick={downloadQr} className="w-full bg-[#1A362D] hover:bg-[#12271f]" data-testid="download-qr-button">
                <Download className="mr-2 h-4 w-4" /> Download PNG
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!assignGuest} onOpenChange={(o) => !o && setAssignGuest(null)}>
        <DialogContent className="max-w-md" data-testid="assign-room-dialog">
          <DialogHeader>
            <DialogTitle>Rooms for {assignGuest?.primary_guest_name}</DialogTitle>
          </DialogHeader>
          {assignGuest && (
            <div className="space-y-4">
              <div className="space-y-2">
                {(assignments.filter((a) => a.guest_id === assignGuest.id) || []).map((a) => {
                  const room = rooms.find((r) => r.id === a.room_id);
                  return (
                    <div key={a.id} className="flex items-center justify-between rounded border px-3 py-2 text-sm">
                      <span>
                        Room {room?.room_number || "?"} · {a.slots_used} guest(s)
                        {a.extra_beds ? ` · ${a.extra_beds} extra bed` : ""}
                        {room?.accessible ? " · accessible" : ""}
                      </span>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={async () => {
                          await api.delete(`/room-assignments/${a.id}`);
                          toast.success("Unassigned");
                          load();
                        }}
                      >
                        Remove
                      </Button>
                    </div>
                  );
                })}
                {assignments.filter((a) => a.guest_id === assignGuest.id).length === 0 && (
                  <p className="text-sm text-[#78716C]">No rooms assigned yet.</p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Add room</Label>
                <Select value={assignRoomId} onValueChange={setAssignRoomId}>
                  <SelectTrigger><SelectValue placeholder="Available room" /></SelectTrigger>
                  <SelectContent>
                    {rooms.filter((r) => !r.held && (r.slots_remaining || 0) > 0).map((r) => (
                      <SelectItem key={r.id} value={r.id}>
                        {r.room_number} · {r.category} · {r.slots_remaining} left
                        {r.accessible ? " · accessible" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Guests in this room</Label>
                <Select value={assignSlots} onValueChange={setAssignSlots}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: Math.max(1, rooms.find((r) => r.id === assignRoomId)?.slots_remaining || 2) }, (_, n) => String(n + 1)).map((n) => (
                      <SelectItem key={n} value={n}>{n}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-[#737373]">A family can be placed in more than one room. Add another room for the remaining guests.</p>
              </div>
              <div className="space-y-2">
                <Label>Extra beds in this room</Label>
                <Select value={assignExtraBeds} onValueChange={setAssignExtraBeds}>
                  <SelectTrigger data-testid="assign-extra-beds"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">None</SelectItem>
                    <SelectItem value="1">1 extra bed</SelectItem>
                    <SelectItem value="2">2 extra beds</SelectItem>
                  </SelectContent>
                </Select>
                {assignGuest?.rsvp?.extra_bed_required && (
                  <p className="text-xs text-[#525252]">Guest asked for {assignGuest.rsvp.extra_beds || 1} extra bed.</p>
                )}
              </div>
              <DialogFooter>
                <Button
                  className="bg-[#1A362D]"
                  onClick={async () => {
                    if (!assignRoomId) return toast.error("Select a room");
                    try {
                      await api.post("/room-assignments", {
                        room_id: assignRoomId,
                        guest_id: assignGuest.id,
                        slots_used: Number(assignSlots) || 1,
                        extra_beds: Number(assignExtraBeds) || 0,
                      });
                      toast.success("Assigned");
                      setAssignRoomId("");
                      load();
                    } catch (e) {
                      toast.error(formatApiError(e.response?.data?.detail) || "Could not assign");
                    }
                  }}
                >
                  Assign
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={extendOpen} onOpenChange={setExtendOpen}>
        <DialogContent data-testid="extend-deadline-dialog">
          <DialogHeader>
            <DialogTitle>Extend RSVP date</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#525252]">
            {Object.values(selected).filter(Boolean).length} selected. Their personal link will stay open through this date, even if the event deadline has passed.
          </p>
          <div className="space-y-2">
            <Label>New RSVP date</Label>
            <Input type="date" value={extendDate} onChange={(e) => setExtendDate(e.target.value)} data-testid="extend-deadline-input" />
          </div>
          <DialogFooter>
            <Button
              className="bg-[#1A362D]"
              disabled={!extendDate}
              onClick={async () => {
                const guest_ids = Object.entries(selected).filter(([, on]) => on).map(([id]) => id);
                try {
                  const { data } = await api.post("/guests/extend-deadline", {
                    event_id: eventId,
                    guest_ids,
                    rsvp_deadline: extendDate,
                  });
                  toast.success(`RSVP date set for ${data.updated} guest(s)`);
                  setExtendOpen(false);
                  setSelected({});
                  load();
                } catch (err) {
                  toast.error(formatApiError(err.response?.data?.detail) || "Could not extend the date");
                }
              }}
              data-testid="confirm-extend-deadline"
            >
              Save date
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!giftGuest} onOpenChange={(open) => !open && setGiftGuest(null)}>
        <DialogContent data-testid="gift-dialog">
          <DialogHeader>
            <DialogTitle>Gift · {giftGuest?.family_name || giftGuest?.primary_guest_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2">
              <Label>Gift</Label>
              <Input
                value={giftForm.description}
                onChange={(e) => setGiftForm({ ...giftForm, description: e.target.value })}
                placeholder="What they gave"
                data-testid="gift-description"
              />
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Input
                value={giftForm.notes}
                onChange={(e) => setGiftForm({ ...giftForm, notes: e.target.value })}
                placeholder="Received, pending, thank-you sent…"
                data-testid="gift-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              className="bg-[#1A362D]"
              onClick={async () => {
                try {
                  await api.patch(`/guests/${giftGuest.id}/gift`, giftForm);
                  toast.success("Gift saved");
                  setGiftGuest(null);
                  load();
                } catch (err) {
                  toast.error(formatApiError(err.response?.data?.detail) || "Could not save gift");
                }
              }}
              data-testid="save-gift"
            >
              Save gift
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
